/**
 * Group create / rename / manage-members dialogs (PRD §1 "Groups section", §5.1 #12-#14).
 *
 * Membership dialogs always send the **full** member set — `PUT /groups/:id/members` replaces it —
 * and a duplicate name comes back as `409 conflict`, whose server message is rendered inline.
 *
 * Each dialog is mounted only while it is open (the parent renders it conditionally), so its draft
 * state is seeded once at mount and needs no reset effects.
 */

import { useState } from 'react';
import {
  GROUP_NAME_MAX_CHARS,
  WATCH_CADENCE_MAX_MINUTES,
  WATCH_CADENCE_MIN_MINUTES,
  type GroupDTO,
  type ProfileRowDTO,
  type WatchTargetBody,
} from '@lw/shared';
import { describeError } from '../../api/client';
import { CheckboxList, Modal, Spinner } from '../../components';
import { pluralize } from '../../lib/format';
import { CadenceQuickPicks } from '../settings/CadenceQuickPicks';
import { useCreateGroup, useSetGroupMembers, useSetWatching, useUpdateGroup } from './useHomeData';

function memberOptions(profiles: ProfileRowDTO[], filter: string) {
  const needle = filter.trim().toLowerCase();
  return profiles
    .filter((profile) => (profile.archivedAt ?? null) === null)
    .filter((profile) =>
      needle.length === 0
        ? true
        : `${profile.name ?? ''} ${profile.slug}`.toLowerCase().includes(needle),
    )
    .map((profile) => ({ value: profile.id, label: profile.name ?? profile.slug }));
}

/* ------------------------------------------------------------------ create */

export type CreateGroupDialogProps = {
  profiles: ProfileRowDTO[];
  onClose: () => void;
  onCreated?: (group: GroupDTO) => void;
};

export function CreateGroupDialog({ profiles, onClose, onCreated }: CreateGroupDialogProps) {
  const [name, setName] = useState('');
  const [memberIds, setMemberIds] = useState<number[]>([]);
  const [filter, setFilter] = useState('');
  const create = useCreateGroup();

  const submit = () => {
    const trimmed = name.trim();
    if (trimmed.length === 0 || create.isPending) return;
    create.mutate(
      { name: trimmed, memberIds: memberIds.length > 0 ? memberIds : undefined },
      {
        onSuccess: (group) => {
          onCreated?.(group);
          onClose();
        },
      },
    );
  };

  return (
    <Modal
      open
      title="New group"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="table-action" onClick={onClose}>
            <span className="table-action__label">Cancel</span>
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={submit}
            disabled={name.trim().length === 0 || create.isPending}
          >
            {create.isPending ? <Spinner size="sm" /> : null}
            Create group
          </button>
        </>
      }
    >
      <div className="stack">
        <div className="field">
          <label className="field__label" htmlFor="group-name">
            Name
          </label>
          <input
            id="group-name"
            className="input"
            value={name}
            maxLength={GROUP_NAME_MAX_CHARS}
            autoFocus
            onChange={(event) => setName(event.target.value)}
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="group-members-filter">
            Members (optional)
          </label>
          <input
            id="group-members-filter"
            className="input"
            placeholder="Filter profiles"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
          />
        </div>

        <CheckboxList
          options={memberOptions(profiles, filter)}
          selected={memberIds}
          onChange={setMemberIds}
          emptyLabel="No active profiles to add yet."
        />

        {create.isError ? (
          <p className="form-error" role="alert">
            {describeError(create.error)}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------ rename */

export type RenameGroupDialogProps = {
  group: GroupDTO;
  onClose: () => void;
};

export function RenameGroupDialog({ group, onClose }: RenameGroupDialogProps) {
  const [name, setName] = useState(group.name);
  const rename = useUpdateGroup();

  const submit = () => {
    const trimmed = name.trim();
    if (trimmed.length === 0 || rename.isPending) return;
    if (trimmed === group.name) {
      onClose();
      return;
    }
    rename.mutate({ id: group.id, body: { name: trimmed } }, { onSuccess: onClose });
  };

  return (
    <Modal
      open
      title="Rename group"
      size="sm"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="table-action" onClick={onClose}>
            <span className="table-action__label">Cancel</span>
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={submit}
            disabled={name.trim().length === 0 || rename.isPending}
          >
            {rename.isPending ? <Spinner size="sm" /> : null}
            Rename
          </button>
        </>
      }
    >
      <div className="stack">
        <div className="field">
          <label className="field__label" htmlFor="group-rename">
            Name
          </label>
          <input
            id="group-rename"
            className="input"
            value={name}
            maxLength={GROUP_NAME_MAX_CHARS}
            autoFocus
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        {rename.isError ? (
          <p className="form-error" role="alert">
            {describeError(rename.error)}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

/* ---------------------------------------------------------------- watching */

/**
 * Group overrides for New Post Watching (SPEC § Homescreen "Group overrides"): the enable toggle
 * the row's switch also writes, plus the two things only a group may override — cadence and the
 * smart-schedule flag. Everything saves through #70 in one call.
 *
 * Both controls are **pre-filled from what is stored**: #11's `watching` block ships the group's
 * `cadenceMinutes`/`smart` (`null` = inherit), so the dialog opens on the truth and every save sends
 * the complete `{enabled, cadenceMinutes, smart}` triple — no "unchanged" third state to reason
 * about, and closing without editing writes back exactly what was already there.
 */
type OverrideChoice = 'inherit' | 'custom';
type SmartChoice = 'inherit' | 'on' | 'off';

export type WatchingGroupDialogProps = {
  group: GroupDTO;
  onClose: () => void;
};

function smartChoiceOf(smart: boolean | null | undefined): SmartChoice {
  if (smart === null || smart === undefined) return 'inherit';
  return smart ? 'on' : 'off';
}

export function WatchingGroupDialog({ group, onClose }: WatchingGroupDialogProps) {
  const storedCadence = group.watching.cadenceMinutes ?? null;
  const [enabled, setEnabled] = useState(group.watching.enabled);
  const [cadenceChoice, setCadenceChoice] = useState<OverrideChoice>(
    storedCadence === null ? 'inherit' : 'custom',
  );
  const [cadenceMinutes, setCadenceMinutes] = useState(String(storedCadence ?? 15));
  const [smart, setSmart] = useState<SmartChoice>(smartChoiceOf(group.watching.smart));
  const save = useSetWatching();

  const parsedCadence = Number(cadenceMinutes);
  const cadenceInvalid =
    cadenceChoice === 'custom' &&
    (cadenceMinutes.trim().length === 0 ||
      !Number.isInteger(parsedCadence) ||
      parsedCadence < WATCH_CADENCE_MIN_MINUTES ||
      parsedCadence > WATCH_CADENCE_MAX_MINUTES);

  const body = (): WatchTargetBody => ({
    enabled,
    cadenceMinutes: cadenceChoice === 'inherit' ? null : parsedCadence,
    smart: smart === 'inherit' ? null : smart === 'on',
  });

  return (
    <Modal
      open
      size="sm"
      title={`Watching — ${group.name}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="table-action" onClick={onClose}>
            <span className="table-action__label">Cancel</span>
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={cadenceInvalid || save.isPending}
            onClick={() =>
              save.mutate({ t: 'group', id: group.id, body: body() }, { onSuccess: onClose })
            }
          >
            {save.isPending ? <Spinner size="sm" /> : null}
            Save watching
          </button>
        </>
      }
    >
      <div className="stack">
        <label className="toggle">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(event) => setEnabled(event.target.checked)}
          />
          <span>Watch every member of this group</span>
        </label>
        <p className="field__hint">{group.watching.hoverDisplay}</p>

        <div className="field">
          <label className="field__label" htmlFor="watch-group-cadence-mode">
            Cadence
          </label>
          <select
            id="watch-group-cadence-mode"
            className="select"
            value={cadenceChoice}
            onChange={(event) => setCadenceChoice(event.target.value as OverrideChoice)}
          >
            <option value="inherit">Inherit the global cadence</option>
            <option value="custom">Every N minutes…</option>
          </select>
          {cadenceChoice === 'custom' ? (
            <>
              <input
                className="input"
                type="number"
                min={WATCH_CADENCE_MIN_MINUTES}
                max={WATCH_CADENCE_MAX_MINUTES}
                step="1"
                aria-label="Minutes between checks"
                value={cadenceMinutes}
                onChange={(event) => setCadenceMinutes(event.target.value)}
              />
              <CadenceQuickPicks
                value={cadenceMinutes}
                onPick={setCadenceMinutes}
                ariaLabel="Cadence presets"
              />
            </>
          ) : null}
          {cadenceInvalid ? (
            <span className="settings-bad">
              Enter a whole number of minutes between {WATCH_CADENCE_MIN_MINUTES} and{' '}
              {WATCH_CADENCE_MAX_MINUTES}.
            </span>
          ) : (
            <span className="field__hint">
              Members watched through several sources use the fastest cadence of them.
            </span>
          )}
        </div>

        <div className="field">
          <label className="field__label" htmlFor="watch-group-smart">
            Smart schedule
          </label>
          <select
            id="watch-group-smart"
            className="select"
            value={smart}
            onChange={(event) => setSmart(event.target.value as SmartChoice)}
          >
            <option value="inherit">Inherit the global setting</option>
            <option value="on">On</option>
            <option value="off">Off</option>
          </select>
          <span className="field__hint">
            Hot hours are learned from each account's posting pattern.
          </span>
        </div>

        {save.isError ? (
          <p className="form-error" role="alert">
            {describeError(save.error)}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

/* ----------------------------------------------------------------- members */

export type ManageMembersDialogProps = {
  group: GroupDTO;
  profiles: ProfileRowDTO[];
  onClose: () => void;
};

export function ManageMembersDialog({ group, profiles, onClose }: ManageMembersDialogProps) {
  const [memberIds, setMemberIds] = useState<number[]>(group.memberIds);
  const [filter, setFilter] = useState('');
  const save = useSetGroupMembers();

  return (
    <Modal
      open
      title={`Members — ${group.name}`}
      onClose={onClose}
      footer={
        <>
          <span className="muted dialog-hint">{pluralize(memberIds.length, 'member')}</span>
          <button type="button" className="table-action" onClick={onClose}>
            <span className="table-action__label">Cancel</span>
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={() => save.mutate({ id: group.id, memberIds }, { onSuccess: onClose })}
            disabled={save.isPending}
          >
            {save.isPending ? <Spinner size="sm" /> : null}
            Save members
          </button>
        </>
      }
    >
      <div className="stack">
        <div className="field">
          <label className="field__label" htmlFor="group-members-manage-filter">
            Filter profiles
          </label>
          <input
            id="group-members-manage-filter"
            className="input"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
          />
        </div>

        <CheckboxList
          options={memberOptions(profiles, filter)}
          selected={memberIds}
          onChange={setMemberIds}
          emptyLabel="No active profiles available."
        />

        {save.isError ? (
          <p className="form-error" role="alert">
            {describeError(save.error)}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}
